import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from 'recharts';
import { useStore } from '../lib/store.js';
import { bytes, bps, pct, uptime, relTime } from '../lib/format.js';
import ServerCard from '../components/ServerCard.js';
import { MetricChart, RangePicker, type Range } from '../components/MetricChart.js';
import { LoadingCenter } from '../components/Spinner.js';

const HISTORY_LEN = 60; // ~2 minutes at 2s telemetry intervals

interface HistorySample { t: number; cpu: number; mem: number; rx: number; tx: number }

export default function MachineDetail() {
  const { name } = useParams();
  const machineName = name ? decodeURIComponent(name) : '';

  const tel = useStore((s) => s.machines.get(machineName));
  const status = useStore((s) => s.nodeStatus.get(machineName));
  const allServers = useStore((s) => Array.from(s.servers.values()));
  const live = useStore((s) => s.liveStats);
  const nodeStatus = useStore((s) => s.nodeStatus);
  const hasSnapshot = useStore((s) => s.hasSnapshot);

  const machineServers = useMemo(
    () => allServers.filter((srv) => srv.Server_Machine === machineName).sort((a, b) => a.Server_Name.localeCompare(b.Server_Name)),
    [allServers, machineName],
  );

  const [history, setHistory] = useState<HistorySample[]>([]);
  const [range, setRange] = useState<Range>('6h');
  const lastTs = useRef<number>(0);
  useEffect(() => {
    if (!tel) return;
    if (tel.ts === lastTs.current) return;
    lastTs.current = tel.ts;
    setHistory((h) => {
      const next: HistorySample = {
        t: tel.ts,
        cpu: tel.cpu.load_pct,
        mem: (tel.memory.used_bytes / tel.memory.total_bytes) * 100,
        rx: tel.network.rx_bytes_per_sec,
        tx: tel.network.tx_bytes_per_sec,
      };
      const out = [...h, next];
      while (out.length > HISTORY_LEN) out.shift();
      return out;
    });
  }, [tel]);

  if (!tel && !status && machineServers.length === 0) {
    if (!hasSnapshot) {
      return <LoadingCenter label={`Loading ${machineName}…`} />;
    }
    return (
      <div className="card p-10 text-center max-w-md mx-auto">
        <h2 className="text-lg font-semibold mb-2">Machine not found</h2>
        <Link to="/" className="text-accent hover:underline">← Back</Link>
      </div>
    );
  }

  const online = !!status?.online;

  return (
    <div className="space-y-6">
      <div className="card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3">
              <span className={`dot dot-lg ${online ? 'bg-status-online' : 'bg-status-error'}`} />
              <span className="truncate">{machineName}</span>
            </h1>
            <div className="text-sm text-fg-muted mt-1.5">
              {tel?.cpu.model ?? 'CPU model unknown'}
              <span className="text-fg-dim mx-1.5">·</span>{tel?.cpu.cores ?? '—'} cores
              <span className="text-fg-dim mx-1.5">·</span>
              {online ? (
                'online'
              ) : (
                <span className="text-status-error">unreachable — last seen {status ? relTime(status.last_seen) : '—'}</span>
              )}
            </div>
          </div>
          <div className="text-right text-xs text-fg-dim space-y-1 shrink-0">
            <div>{machineServers.length} server{machineServers.length === 1 ? '' : 's'}</div>
            {online && tel && <div>Uptime {uptime(tel.uptime_sec)}</div>}
          </div>
        </div>
      </div>

      {!online && (
        <div className="card p-4 border-status-error/40 bg-status-error/5 text-sm text-fg-muted">
          <strong className="text-status-error">Agent disconnected.</strong> Live telemetry is paused; the
          history charts below show data through the last frame received. Real-time stats will resume when
          the machine reconnects.
        </div>
      )}

      {online && tel ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <SparkTile label="CPU" value={pct(tel.cpu.load_pct)} data={history} dataKey="cpu" max={100} />
          <SparkTile label="Memory" value={`${pct((tel.memory.used_bytes / tel.memory.total_bytes) * 100)}`} sub={bytes(tel.memory.used_bytes)} data={history} dataKey="mem" max={100} />
          <SparkTile label="Net In" value={bps(tel.network.rx_bytes_per_sec)} data={history} dataKey="rx" />
          <SparkTile label="Net Out" value={bps(tel.network.tx_bytes_per_sec)} data={history} dataKey="tx" />
        </div>
      ) : online ? (
        <div className="card p-10 text-center text-fg-muted">No telemetry yet — waiting for first frame.</div>
      ) : null}

      <section className="space-y-3">
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-fg">History</h2>
            <p className="text-xs text-fg-dim mt-0.5">30s samples · retained 7 days</p>
          </div>
          <RangePicker value={range} onChange={setRange} />
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <MetricChart endpoint={`/api/metrics/machine/${encodeURIComponent(machineName)}`} range={range} dataKey="cpu_pct" label="CPU"    stroke="#34d399" max={100} format={(n) => n == null ? '—' : `${n.toFixed(0)}%`} />
          <MetricChart endpoint={`/api/metrics/machine/${encodeURIComponent(machineName)}`} range={range} dataKey="mem_pct" label="Memory" stroke="#f5b544" max={100} format={(n) => n == null ? '—' : `${n.toFixed(0)}%`} />
          <MetricChart endpoint={`/api/metrics/machine/${encodeURIComponent(machineName)}`} range={range} dataKey="rx_bps"  label="Net In"  stroke="#5fa8ff" format={(n) => n == null ? '—' : bps(n)} />
          <MetricChart endpoint={`/api/metrics/machine/${encodeURIComponent(machineName)}`} range={range} dataKey="tx_bps"  label="Net Out" stroke="#a78bfa" format={(n) => n == null ? '—' : bps(n)} />
        </div>
      </section>

      {online && tel && tel.disks.length > 0 && (
        <section className="card p-5">
          <h2 className="font-semibold mb-4 text-fg">Disks</h2>
          <div className="space-y-3">
            {tel.disks.map((d) => {
              const usedPct = (d.used_bytes / d.total_bytes) * 100;
              const color = usedPct > 90 ? 'bg-status-error' : usedPct > 75 ? 'bg-status-warn' : 'bg-accent';
              return (
                <div key={d.mount} className="flex items-center gap-3 text-sm flex-wrap sm:flex-nowrap">
                  <span className="font-mono text-fg-muted w-16 shrink-0">{d.mount}</span>
                  <div className="flex-1 h-2 bg-bg-border rounded-full overflow-hidden min-w-[120px]">
                    <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, usedPct)}%` }} />
                  </div>
                  <span className="tabular-nums text-fg-muted w-44 text-right text-xs shrink-0">
                    {bytes(d.used_bytes)} / {bytes(d.total_bytes)} <span className="text-fg-dim">({pct(usedPct)})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-base font-semibold mb-4 text-fg">Servers on this machine</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {machineServers.map((s) => (
            <ServerCard key={s.Server_ID} server={s} live={live.get(s.Server_ID)} nodeOnline={!!nodeStatus.get(s.Server_Machine)?.online} />
          ))}
          {machineServers.length === 0 && (
            <div className="card p-8 col-span-full text-center text-fg-muted">No servers on this machine.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function SparkTile({
  label, value, sub, data, dataKey, max,
}: {
  label: string;
  value: string;
  sub?: string;
  data: HistorySample[];
  dataKey: keyof HistorySample;
  max?: number;
}) {
  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-fg-dim font-semibold">{label}</div>
      <div className="flex items-baseline justify-between gap-2 mt-1.5 mb-3">
        <div className="text-2xl font-semibold tabular-nums tracking-tight text-fg">{value}</div>
        {sub && <div className="text-[11px] text-fg-dim tabular-nums">{sub}</div>}
      </div>
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <YAxis hide domain={max ? [0, max] : ['auto', 'auto']} />
            <Tooltip
              contentStyle={{ background: '#161a24', border: '1px solid #262b3a', borderRadius: 8, fontSize: 11, padding: '4px 8px' }}
              labelFormatter={() => ''}
              formatter={(v: number) => (typeof v === 'number' ? v.toFixed(1) : v)}
            />
            <Line type="monotone" dataKey={dataKey as string} stroke="#34d399" strokeWidth={1.75} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
